import { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';

import { type RenderFlyoutOptions } from 'common/visualizations/views/FlyoutRenderer';

import { flyoutRenderer } from 'lib/components/FlyoutRenderer';
import StoryVisualization from 'lib/components/StoryVisualization';
import Constants from 'lib/Constants';
import StorytellerUtils from 'lib/StorytellerUtils';
import { StorytellerReduxStoreFactory } from 'store/StorytellerReduxStore';
import { type BlockComponent, ComponentType, type InSituVisualizationBlockComponent } from 'types';

import { type ReactRendererProps } from '../shared/ComponentRegistry';

export const resizableProps = (componentData: BlockComponent) => {
  const isPie = componentData.type === ComponentType.PIE_CHART;

  return {
    resizeSupported: true,
    resizeOptions: {
      minHeight: isPie
        ? Constants.MINIMUM_COMPONENT_HEIGHTS_PX.PIE_CHART
        : Constants.MINIMUM_COMPONENT_HEIGHTS_PX.VISUALIZATION,
    },
    defaultHeight: isPie ? Constants.DEFAULT_PIE_CHART_HEIGHT : undefined,
  };
};

/**
 * React renderer for the in-situ chart types (bar, calendar, column, combo, histogram,
 * pie, scatter, timeline). Chart-type-specific rendering is delegated to StoryVisualization.
 */
export const ComponentInSituVisualization = ({ componentData }: ReactRendererProps) => {
  const { type, value } = componentData as InSituVisualizationBlockComponent;
  const typeClasses = StorytellerUtils.typeToClassesForComponentType(type);
  const vif = value?.vif;
  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = componentRef.current;
    if (!element) {
      return;
    }

    const handleFlyout = (event: Event) => {
      const payload = (event as CustomEvent<RenderFlyoutOptions | null>).detail;
      if (payload !== null) {
        flyoutRenderer.render(payload);
      } else {
        flyoutRenderer.clear();
      }
    };

    element.addEventListener('SOCRATA_VISUALIZATION_FLYOUT', handleFlyout);

    return () => element.removeEventListener('SOCRATA_VISUALIZATION_FLYOUT', handleFlyout);
  }, [vif]);

  if (!vif) {
    return null;
  }

  return (
    <div
      className={`component ${typeClasses}`}
      ref={componentRef}
      data-testid="component-in-situ-visualization"
    >
      <div className="component-content story-viz-react">
        <Provider store={StorytellerReduxStoreFactory()}>
          <StoryVisualization storyVif={vif} />
        </Provider>
      </div>
    </div>
  );
};
